/**
 * The client's export facts (e2e-filing B-01, B-03; FR-E9, FR-E10): the Letters of
 * Undertaking it has furnished, and the buyers a person confirmed as SEZ units.
 *
 * **Why on the profile.** An export books WPAY or WOPAY — and an SEZ supply SEWP or SEWOP —
 * by the LUT for its invoice's financial year (D-143). The LUT is the client's, furnished
 * once a year, so it is recorded here once, a year at a time, rather than answered on every
 * return. One made without tax in a year with no LUT holds.
 *
 * **An SEZ buyer is never derived silently (D-16).** The register lookup may say a GSTIN is
 * an SEZ unit; a person still confirms it, and the card says which it was. With the lookup
 * off, a person can confirm on their own word, and the card says that too.
 */
import { Alert, Button, Card, Input } from '@fiveox/ui'
import { useCallback, useEffect, useState } from 'react'

import { CLIENT, useEvaluation } from '../lib/access'
import { ApiError, type ExportFacts, clients, exportFacts } from '../lib/api'
import {
  type ExportFactsSection,
  type SectionErrors,
  financialYearOf,
  lutProblem,
  lutWords,
  registerSaysSez,
  sezGstinProblem,
  sezSourceWords,
  withoutError,
} from '../lib/exportFacts'

const NO_LUT = { number: '', financial_year: '' }

export function ExportFactsCard({ clientId, ownGstin }: { clientId: string; ownGstin: string | null }) {
  const { can } = useEvaluation()
  const editable = can(CLIENT, 'can_update')

  const [facts, setFacts] = useState<ExportFacts | null>(null)
  // N31: each error beside the part it is about, and gone when that part's input changes.
  const [errors, setErrors] = useState<SectionErrors>({})
  const fail = (section: ExportFactsSection, words: string) =>
    setErrors((e) => ({ ...e, [section]: words }))
  const edited = (section: ExportFactsSection) => setErrors((e) => withoutError(e, section))
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [lut, setLut] = useState(NO_LUT)
  const [gstin, setGstin] = useState('')
  /** What the register said about the GSTIN being added, once looked up. */
  const [lookedUp, setLookedUp] = useState<{ gstin: string; says: string | null } | null>(null)

  const load = useCallback(async () => {
    try {
      setFacts((await exportFacts.get(clientId)).data)
    } catch (err) {
      fail('load', err instanceof ApiError ? err.message : 'The export details could not be read.')
    }
  }, [clientId])

  useEffect(() => {
    void load()
  }, [load])

  const act = useCallback(async (section: ExportFactsSection, fn: () => Promise<{ data: ExportFacts }>) => {
    setBusy(true)
    setErrors((e) => withoutError(e, section))
    setNotice(null)
    try {
      const body = (await fn()).data
      setFacts(body)
      if (body.demoted_periods.length) {
        setNotice(
          `Sent back for re-approval because their exports book differently now: ${body.demoted_periods.join(', ')}.`,
        )
      }
      return true
    } catch (err) {
      fail(section, err instanceof ApiError ? err.message : 'That did not save.')
      return false
    } finally {
      setBusy(false)
    }
  }, [])

  const lookUp = useCallback(async () => {
    setBusy(true)
    setErrors((e) => withoutError(e, 'sez'))
    try {
      const found = (await clients.lookup(gstin.trim().toUpperCase())).data
      setLookedUp({ gstin: found.gstin, says: found.register?.registration_type ?? null })
    } catch (err) {
      fail('sez', err instanceof ApiError ? err.message : 'The GSTIN could not be looked up.')
    } finally {
      setBusy(false)
    }
  }, [gstin])

  if (!facts) {
    return errors.load ? (
      <Card className="p-4">
        <p className="gst-panel-h mb-3">Exports and SEZ</p>
        <Alert tone="error">{errors.load}</Alert>
      </Card>
    ) : null
  }

  const problem = lutProblem(lut)
  const gstinProblem = gstin.trim() ? sezGstinProblem(gstin, ownGstin) : null
  const sameLookup = lookedUp && lookedUp.gstin === gstin.trim().toUpperCase()

  return (
    <Card className="p-4">
      <p className="gst-panel-h mb-3">Exports and SEZ</p>
      {errors.load && (
        <Alert tone="error" className="mb-3">
          {errors.load}
        </Alert>
      )}
      {notice && (
        <Alert tone="warning" className="mb-3">
          {notice}
        </Alert>
      )}

      <p className="gst-meta mb-2">
        Letters of Undertaking, one for each financial year. An export or a supply to an SEZ
        invoiced without tax is filed under the LUT for its invoice&rsquo;s year; with none
        recorded for that year it is held until one is, or the IGST is charged.
      </p>
      {facts.luts.length ? (
        <ul className="mb-3 grid gap-1" data-testid="luts">
          {facts.luts.map((l) => (
            <li key={l.financial_year} className="flex items-baseline justify-between gap-3">
              <span>{lutWords(l)}</span>
              {editable && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    void act('lut', () => exportFacts.removeLut(clientId, l.financial_year))
                  }
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-3">No LUT is recorded for this client.</p>
      )}
      {editable && (
        <div className="mb-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="LUT number"
              value={lut.number}
              onChange={(e) => {
                setLut((d) => ({ ...d, number: e.target.value }))
                edited('lut')
              }}
            />
            <Input
              label="Financial year"
              placeholder="2026-27"
              value={lut.financial_year}
              onChange={(e) => {
                setLut((d) => ({ ...d, financial_year: e.target.value }))
                edited('lut')
              }}
            />
          </div>
          {problem && (lut.number || lut.financial_year) && (
            <p className="gst-meta mt-2">{problem}</p>
          )}
          {errors.lut && (
            <Alert tone="error" className="mt-2">
              {errors.lut}
            </Alert>
          )}
          <Button
            type="button"
            size="sm"
            className="mt-3"
            disabled={busy || problem !== null}
            onClick={() =>
              void act('lut', () =>
                exportFacts.addLut(clientId, {
                  number: lut.number.trim(),
                  financial_year: financialYearOf(lut.financial_year) ?? lut.financial_year,
                }),
              ).then(
                (ok) => ok && setLut(NO_LUT),
              )
            }
          >
            {busy ? 'Saving…' : 'Record LUT'}
          </Button>
        </div>
      )}

      <p className="gst-meta mb-2">
        Buyers that are SEZ units or developers. Their invoices file in Table 6B, with or
        without payment by the LUT. A buyer is only treated as an SEZ once a person confirms
        it.
      </p>
      {facts.sez_buyers.length ? (
        <ul className="mb-3 grid gap-1" data-testid="sez-buyers">
          {facts.sez_buyers.map((b) => (
            <li key={b.gstin} className="flex items-baseline justify-between gap-3">
              <span>
                <span className="gst-mono">{b.gstin}</span>
                <span className="gst-meta ml-2">{sezSourceWords(b)}</span>
              </span>
              {editable && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => void act('sez', () => exportFacts.removeSez(clientId, b.gstin))}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-3">No buyer is confirmed as an SEZ.</p>
      )}
      {editable && (
        <div>
          <Input
            label="Buyer GSTIN"
            value={gstin}
            onChange={(e) => {
              setGstin(e.target.value)
              setLookedUp(null)
              edited('sez')
            }}
          />
          {gstinProblem && <p className="gst-meta mt-2">{gstinProblem}</p>}
          {errors.sez && (
            <Alert tone="error" className="mt-2">
              {errors.sez}
            </Alert>
          )}
          {sameLookup && (
            <p className="gst-meta mt-2" data-testid="sez-lookup">
              {lookedUp.says === null
                ? 'The GST register could not be read for this GSTIN, so it says nothing either way.'
                : registerSaysSez(lookedUp.says)
                  ? `The GST register says “${lookedUp.says}”.`
                  : `The GST register says “${lookedUp.says}”, not an SEZ.`}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy || !gstin.trim() || gstinProblem !== null}
              onClick={() => void lookUp()}
            >
              Look up on the GST register
            </Button>
            {sameLookup && registerSaysSez(lookedUp.says) && (
              <Button
                type="button"
                size="sm"
                disabled={busy}
                onClick={() =>
                  void act('sez', () =>
                    exportFacts.confirmSez(clientId, {
                      gstin: gstin.trim().toUpperCase(),
                      derived_from: 'register',
                      register_says: lookedUp.says,
                    }),
                  ).then((ok) => ok && setGstin(''))
                }
              >
                Confirm as an SEZ buyer
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy || !gstin.trim() || gstinProblem !== null}
              onClick={() =>
                void act('sez', () =>
                  exportFacts.confirmSez(clientId, {
                    gstin: gstin.trim().toUpperCase(),
                    derived_from: 'person',
                  }),
                ).then((ok) => ok && setGstin(''))
              }
            >
              Confirm on my own word
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}
